import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Clock, User, Briefcase, LogIn, LogOut, Users, TrendingUp,
  CheckCircle, AlertCircle, Search, X, ChevronDown, Filter,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_REGISTRATIONS_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/time-registration/registrations/`;
const API_SESSIONS_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/time-registration/sessions/`;
const API_USERS_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/users/`;
const API_PROJECTS_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/projects/`;

// Stat Card Component (OEM Style)
const StatCard = ({ icon: Icon, value, label, subtitle, iconBg, valueColor }) => (
  <div className="bg-white rounded-2xl p-4 border border-steel-100 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg || 'bg-steel-100'}`}>
        <Icon className="h-5 w-5 text-steel-600" />
      </div>
      <div>
        <p className={`text-2xl font-black ${valueColor || 'text-slate-900'}`}>{value}</p>
        <p className="text-[10px] font-bold text-steel-400 uppercase tracking-widest">{label}</p>
        {subtitle && <p className="text-[10px] text-steel-500">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// Summary Section with Chart
const TimesheetSummary = ({ filteredSessions, users, lastUpdated, isRefreshing }) => {
  const [timeSinceUpdate, setTimeSinceUpdate] = useState('');

  useEffect(() => {
    const updateTimeSinceUpdate = () => {
      if (!lastUpdated) { setTimeSinceUpdate(''); return; }
      const seconds = Math.floor((new Date() - lastUpdated) / 1000);
      setTimeSinceUpdate(seconds < 60 ? `${seconds}s ago` : `${Math.floor(seconds / 60)}m ago`);
    };
    updateTimeSinceUpdate();
    const interval = setInterval(updateTimeSinceUpdate, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const stats = filteredSessions.reduce(
    (acc, session) => {
      const hours = session.work_hours || 0;
      acc.totalHours += hours;
      if (session.is_complete) { acc.completedSessions += 1; acc.completedHours += hours; }
      else { acc.inProgressSessions += 1; }
      return acc;
    },
    { totalHours: 0, completedSessions: 0, inProgressSessions: 0, completedHours: 0 }
  );

  // Chart data - last 14 days
  const today = new Date();
  const last14Days = Array.from({ length: 14 }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (13 - i));
    return date.toISOString().split('T')[0];
  });

  const sessionsByDate = filteredSessions.reduce((acc, session) => {
    const dateValue = session.check_in_time || session.date;
    if (dateValue) {
      const sessionDate = new Date(dateValue).toISOString().split('T')[0];
      if (!acc[sessionDate]) acc[sessionDate] = { completed: 0, inProgress: 0, total: 0 };
      if (session.is_complete) acc[sessionDate].completed += 1;
      else acc[sessionDate].inProgress += 1;
      acc[sessionDate].total += 1;
    }
    return acc;
  }, {});

  const chartData = last14Days.map(date => {
    const data = sessionsByDate[date] || { completed: 0, inProgress: 0, total: 0 };
    const dateObj = new Date(date);
    return {
      date: `${dateObj.getDate()}/${dateObj.getMonth() + 1}`,
      fullDate: date,
      Completed: data.completed,
      'In Progress': data.inProgress,
      Total: data.total
    };
  });

  // Top users
  const userStats = filteredSessions.reduce((acc, session) => {
    const userId = session.user;
    if (!acc[userId]) acc[userId] = { hours: 0, sessions: 0 };
    acc[userId].hours += session.work_hours || 0;
    acc[userId].sessions += 1;
    return acc;
  }, {});

  const topUsers = Object.entries(userStats)
    .map(([userId, data]) => ({ user: users.find(u => u.id === parseInt(userId)), ...data }))
    .filter(item => item.user)
    .sort((a, b) => b.hours - a.hours)
    .slice(0, 3);

  return (
    <div className="space-y-4 mb-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard icon={Clock} value={`${stats.totalHours.toFixed(1)}h`} label="Total Hours" iconBg="bg-brand-100" />
        <StatCard icon={CheckCircle} value={stats.completedSessions} label="Completed" iconBg="bg-emerald-100" valueColor="text-emerald-600" />
        <StatCard icon={AlertCircle} value={stats.inProgressSessions} label="In Progress" iconBg="bg-amber-100" valueColor="text-amber-600" />
        <StatCard icon={Users} value={new Set(filteredSessions.map(s => s.user)).size} label="Active Users" iconBg="bg-violet-100" />
      </div>

      {/* Chart and Top Users */}
      <div className="grid grid-cols-3 gap-4">
        {/* Chart */}
        <div className="col-span-2 bg-white rounded-2xl border border-steel-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-brand-500" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase">Sessions Trend</h3>
            </div>
            {timeSinceUpdate && (
              <span className="text-[10px] text-steel-400 flex items-center gap-1">
                {isRefreshing && <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />}
                Updated {timeSinceUpdate}
              </span>
            )}
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '10px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '10px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Line type="monotone" dataKey="Completed" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} />
                <Line type="monotone" dataKey="In Progress" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} />
                <Line type="monotone" dataKey="Total" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-white rounded-2xl border border-steel-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
              <Users className="h-4 w-4 text-violet-600" />
            </div>
            <h3 className="text-sm font-black text-slate-900 uppercase">Top Users</h3>
          </div>
          <div className="space-y-3">
            {topUsers.map((item, index) => (
              <div key={item.user.id} className="flex items-center gap-3 p-2 bg-steel-50 rounded-xl">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-white ${
                  index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-steel-400' : 'bg-brand-400'
                }`}>
                  #{index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{item.user.first_name} {item.user.last_name}</p>
                  <p className="text-[10px] text-steel-500">{item.hours.toFixed(1)}h / {item.sessions} sessions</p>
                </div>
              </div>
            ))}
            {topUsers.length === 0 && (
              <p className="text-xs text-steel-400 text-center py-4">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Timesheets = () => {
  const [workSessions, setWorkSessions] = useState([]);
  const [timeRegistrations, setTimeRegistrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState('sessions');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'check_in__timestamp', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [regCurrentPage, setRegCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    else setIsRefreshing(true);
    try {
      const token = localStorage.getItem('access_token');
      const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
      const [sessionsResponse, usersResponse, projectsResponse, registrationsResponse] = await Promise.all([
        axios.get(API_SESSIONS_URL, config),
        axios.get(API_USERS_URL, config),
        axios.get(API_PROJECTS_URL, config),
        axios.get(API_REGISTRATIONS_URL, config)
      ]);
      setWorkSessions(sessionsResponse.data.results || sessionsResponse.data || []);
      setUsers(usersResponse.data.results || usersResponse.data || []);
      setProjects(projectsResponse.data.results || projectsResponse.data || []);
      setTimeRegistrations(registrationsResponse.data.results || registrationsResponse.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (showLoading) setLoading(false);
      else setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchData(true); }, []);
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowProjectDropdown(false);
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDateString = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const filteredSessions = useMemo(() => {
    return workSessions
      .filter(session => {
        const sessionDateStr = getDateString(session.check_in_time || session.date);
        const isCompleted = session.status === 'completed' || session.is_complete;
        const matchesStatus = !selectedStatus || (selectedStatus === 'completed' && isCompleted) || (selectedStatus === 'in_progress' && !isCompleted);
        const matchesProject = selectedProjects.length === 0 || selectedProjects.includes(session.project);
        const matchesUser = selectedUsers.length === 0 || selectedUsers.includes(session.user);
        const matchesStartDate = !startDate || !sessionDateStr || sessionDateStr >= startDate;
        const matchesEndDate = !endDate || !sessionDateStr || sessionDateStr <= endDate;
        return matchesStatus && matchesProject && matchesUser && matchesStartDate && matchesEndDate;
      })
      .sort((a, b) => {
        const getNestedValue = (obj, key) => {
          if (key.includes('__')) {
            const [parentKey, childKey] = key.split('__');
            return obj[parentKey] ? obj[parentKey][childKey] : '';
          }
          return obj[key];
        };
        const aValue = getNestedValue(a, sortConfig.key);
        const bValue = getNestedValue(b, sortConfig.key);
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [workSessions, selectedProjects, selectedUsers, selectedStatus, startDate, endDate, sortConfig]);

  const totalItems = filteredSessions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); setRegCurrentPage(1); }, [selectedProjects, selectedUsers, selectedStatus, startDate, endDate]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]);
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return 'N/A';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const hasFilters = selectedProjects.length > 0 || selectedUsers.length > 0 || selectedStatus || startDate || endDate;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-steel-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-steel-600 text-sm font-medium">Loading timesheets...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-steel-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-steel-100 flex items-center justify-center">
            <Filter className="h-4 w-4 text-steel-600" />
          </div>
          <h3 className="text-sm font-black text-slate-900 uppercase">Filters</h3>
          <span className="ml-auto px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">
            {filteredSessions.length} / {workSessions.length}
          </span>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          {/* Project Filter */}
          <div className="relative filter-dropdown">
            <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1">Project</label>
            <button
              onClick={() => { setShowProjectDropdown(!showProjectDropdown); setShowUserDropdown(false); }}
              className={`h-9 px-3 min-w-[160px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${
                selectedProjects.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
              }`}
            >
              <span className="truncate">{selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} selected`}</span>
              <ChevronDown size={14} />
            </button>
            {showProjectDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="p-2 border-b border-steel-100">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {projects.filter(p => p.is_active).filter(p => !projectSearchQuery || p.name.toLowerCase().includes(projectSearchQuery.toLowerCase())).map(project => (
                    <label key={project.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${selectedProjects.includes(project.id) ? 'bg-brand-50' : ''}`}>
                      <input type="checkbox" checked={selectedProjects.includes(project.id)} onChange={() => handleProjectToggle(project.id)} className="w-4 h-4 text-brand-500 rounded" />
                      <span className="text-sm font-medium text-slate-700 truncate">{project.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User Filter */}
          <div className="relative filter-dropdown">
            <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1">User</label>
            <button
              onClick={() => { setShowUserDropdown(!showUserDropdown); setShowProjectDropdown(false); }}
              className={`h-9 px-3 min-w-[160px] flex items-center justify-between gap-2 border rounded-xl text-sm font-medium transition-colors ${
                selectedUsers.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
              }`}
            >
              <span className="truncate">{selectedUsers.length === 0 ? 'All Users' : `${selectedUsers.length} selected`}</span>
              <ChevronDown size={14} />
            </button>
            {showUserDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="p-2 border-b border-steel-100">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {users.filter(u => !userSearchQuery || `${u.first_name} ${u.last_name}`.toLowerCase().includes(userSearchQuery.toLowerCase())).map(user => (
                    <label key={user.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${selectedUsers.includes(user.id) ? 'bg-brand-50' : ''}`}>
                      <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleUserToggle(user.id)} className="w-4 h-4 text-brand-500 rounded" />
                      <span className="text-sm font-medium text-slate-700 truncate">{user.first_name} {user.last_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={`h-9 px-3 min-w-[120px] border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${
                selectedStatus ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
              }`}
            >
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${
                startDate ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
              }`}
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${
                endDate ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
              }`}
            />
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={() => { setSelectedProjects([]); setSelectedUsers([]); setSelectedStatus(''); setStartDate(''); setEndDate(''); }}
              className="h-9 px-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors flex items-center gap-1"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView('sessions')}
          className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
            view === 'sessions' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'
          }`}
        >
          Work Sessions
        </button>
        <button
          onClick={() => setView('registrations')}
          className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
            view === 'registrations' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'
          }`}
        >
          Time Registrations
        </button>
      </div>

      {/* Work Sessions View */}
      {view === 'sessions' && (
        <>
          <TimesheetSummary filteredSessions={filteredSessions} users={users} lastUpdated={lastUpdated} isRefreshing={isRefreshing} />

          {/* Table */}
          <div className="bg-white rounded-2xl border border-steel-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-steel-50 border-b border-steel-100">
                    <th onClick={() => handleSort('user__first_name')} className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider cursor-pointer hover:text-slate-700">User</th>
                    <th onClick={() => handleSort('project__name')} className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider cursor-pointer hover:text-slate-700">Project</th>
                    <th onClick={() => handleSort('check_in__timestamp')} className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider cursor-pointer hover:text-slate-700">Date</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">Check In</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">Check Out</th>
                    <th onClick={() => handleSort('work_hours')} className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider cursor-pointer hover:text-slate-700">Hours</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold text-steel-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-steel-100">
                  {paginatedSessions.map((session) => {
                    const sessionUser = users.find(u => u.id === session.user);
                    const userAvatar = sessionUser?.profile_image || sessionUser?.avatar;
                    const userName = session.user_name || (sessionUser ? `${sessionUser.first_name || ''} ${sessionUser.last_name || ''}`.trim() : '') || 'Unknown';
                    const userInitials = userName.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2);

                    return (
                    <tr key={session.id} className="hover:bg-steel-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {userAvatar ? (
                            <img src={userAvatar} alt={userName} className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                              {userInitials || 'U'}
                            </div>
                          )}
                          <span className="text-sm font-bold text-slate-900">{userName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-steel-600">{session.project_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-steel-600">{formatDate(session.date || session.check_in_time)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-emerald-600">
                          <LogIn size={14} />
                          <span className="font-medium">{formatTime(session.check_in_time) || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-red-500">
                          <LogOut size={14} />
                          <span className="font-medium">{formatTime(session.check_out_time) || 'In Progress'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">{session.work_hours ? `${session.work_hours.toFixed(2)}h` : 'N/A'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${
                          session.status === 'completed' || session.is_complete
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-amber-50 text-amber-600'
                        }`}>
                          <span className={`w-1 h-1 rounded-full ${session.status === 'completed' || session.is_complete ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          {session.status === 'completed' || session.is_complete ? 'Completed' : 'In Progress'}
                        </span>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-steel-100 flex items-center justify-between">
                <span className="text-[10px] text-steel-500 font-medium">
                  Showing <span className="font-bold text-slate-700">{startIndex + 1}-{Math.min(endIndex, totalItems)}</span> of <span className="font-bold text-slate-700">{totalItems}</span>
                </span>
                <div className="flex items-center gap-1">
                  {/* First Page */}
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  {/* Previous Page */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1 mx-1">
                    {(() => {
                      const pages = [];
                      const showPages = 5;
                      let start = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let end = Math.min(totalPages, start + showPages - 1);
                      if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);

                      if (start > 1) {
                        pages.push(
                          <button key={1} onClick={() => setCurrentPage(1)} className="h-8 w-8 flex items-center justify-center text-xs font-bold text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors">1</button>
                        );
                        if (start > 2) pages.push(<span key="dots1" className="text-steel-400 px-1">...</span>);
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`h-8 w-8 flex items-center justify-center text-xs font-bold rounded-lg transition-colors ${
                              currentPage === i
                                ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                                : 'text-steel-600 bg-steel-100 hover:bg-steel-200'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (end < totalPages) {
                        if (end < totalPages - 1) pages.push(<span key="dots2" className="text-steel-400 px-1">...</span>);
                        pages.push(
                          <button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="h-8 w-8 flex items-center justify-center text-xs font-bold text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors">{totalPages}</button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                  {/* Last Page */}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Time Registrations View */}
      {view === 'registrations' && (() => {
        const filteredRegistrations = timeRegistrations.filter(reg => {
          const regDateStr = getDateString(reg.timestamp);
          const matchesProject = selectedProjects.length === 0 || selectedProjects.includes(reg.project);
          const matchesUser = selectedUsers.length === 0 || selectedUsers.includes(reg.user);
          const matchesStartDate = !startDate || !regDateStr || regDateStr >= startDate;
          const matchesEndDate = !endDate || !regDateStr || regDateStr <= endDate;
          return matchesProject && matchesUser && matchesStartDate && matchesEndDate;
        });
        const regTotalItems = filteredRegistrations.length;
        const regTotalPages = Math.ceil(regTotalItems / itemsPerPage);
        const regStartIndex = (regCurrentPage - 1) * itemsPerPage;
        const regEndIndex = regStartIndex + itemsPerPage;
        const paginatedRegistrations = filteredRegistrations.slice(regStartIndex, regEndIndex);

        return (
        <div className="bg-white rounded-2xl border border-steel-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-steel-50 border-b border-steel-100">
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-steel-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-steel-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-steel-500 uppercase tracking-wider">Photo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-steel-100">
                {paginatedRegistrations.map((registration) => {
                    const regUser = users.find(u => u.id === registration.user);
                    const userAvatar = regUser?.profile_image || regUser?.avatar;
                    const userName = registration.user_name || (regUser ? `${regUser.first_name || ''} ${regUser.last_name || ''}`.trim() : '') || 'Unknown';
                    const userInitials = userName.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2);

                    return (
                    <tr key={registration.id} className="hover:bg-steel-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {userAvatar ? (
                            <img src={userAvatar} alt={userName} className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                              {userInitials || 'U'}
                            </div>
                          )}
                          <span className="text-sm font-bold text-slate-900">{userName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-steel-600">{registration.project_name || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-1.5 text-sm ${registration.registration_type === 'check_in' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {registration.registration_type === 'check_in' ? <LogIn size={14} /> : <LogOut size={14} />}
                          <span className="font-medium capitalize">{registration.registration_type.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-steel-600">
                        {formatDate(registration.timestamp)} {formatTime(registration.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${
                          registration.is_verified ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                        }`}>
                          <span className={`w-1 h-1 rounded-full ${registration.is_verified ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          {registration.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {registration.photo ? (
                          <span className="text-emerald-500 font-bold">Yes</span>
                        ) : (
                          <span className="text-steel-400">-</span>
                        )}
                      </td>
                    </tr>
                  );})}
              </tbody>
            </table>
          </div>

          {/* Pagination for Registrations */}
          {regTotalPages > 1 && (
            <div className="px-4 py-3 border-t border-steel-100 flex items-center justify-between">
              <span className="text-[10px] text-steel-500 font-medium">
                Showing <span className="font-bold text-slate-700">{regStartIndex + 1}-{Math.min(regEndIndex, regTotalItems)}</span> of <span className="font-bold text-slate-700">{regTotalItems}</span>
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setRegCurrentPage(1)} disabled={regCurrentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="First page">
                  <ChevronsLeft size={16} />
                </button>
                <button onClick={() => setRegCurrentPage(prev => Math.max(1, prev - 1))} disabled={regCurrentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Previous page">
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-1 mx-1">
                  {(() => {
                    const pages = [];
                    const showPages = 5;
                    let start = Math.max(1, regCurrentPage - Math.floor(showPages / 2));
                    let end = Math.min(regTotalPages, start + showPages - 1);
                    if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);
                    if (start > 1) {
                      pages.push(<button key={1} onClick={() => setRegCurrentPage(1)} className="h-8 w-8 flex items-center justify-center text-xs font-bold text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors">1</button>);
                      if (start > 2) pages.push(<span key="dots1" className="text-steel-400 px-1">...</span>);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button key={i} onClick={() => setRegCurrentPage(i)} className={`h-8 w-8 flex items-center justify-center text-xs font-bold rounded-lg transition-colors ${regCurrentPage === i ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-steel-600 bg-steel-100 hover:bg-steel-200'}`}>{i}</button>
                      );
                    }
                    if (end < regTotalPages) {
                      if (end < regTotalPages - 1) pages.push(<span key="dots2" className="text-steel-400 px-1">...</span>);
                      pages.push(<button key={regTotalPages} onClick={() => setRegCurrentPage(regTotalPages)} className="h-8 w-8 flex items-center justify-center text-xs font-bold text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors">{regTotalPages}</button>);
                    }
                    return pages;
                  })()}
                </div>
                <button onClick={() => setRegCurrentPage(prev => Math.min(regTotalPages, prev + 1))} disabled={regCurrentPage === regTotalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Next page">
                  <ChevronRight size={16} />
                </button>
                <button onClick={() => setRegCurrentPage(regTotalPages)} disabled={regCurrentPage === regTotalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Last page">
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      );})()}
    </div>
  );
};

export default Timesheets;
